import {
	Button,
	Checkbox as MantineCheckbox,
	Flex,
	Text as MantineText,
	Title,
} from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import {
	DeclarationType,
	GetKYCInvestorInstanceRes,
	KycWorkflowSettingResponseDto,
} from '@dealstack/shared/lib/generated';
import {
	useTranslationInvestor,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Circle } from '@dealstack/frontend/shared/components/common/Circle';
import { GreyBox } from '@dealstack/frontend/shared/components/common/GreyBox';
import { FormCheckbox } from '@dealstack/frontend/shared/components/forms/FormCheckbox/FormCheckbox';
import { CheckboxGroupV2 } from '@dealstack/frontend/shared/components/forms/CheckboxGroup/CheckboxGroup.v2';
import { TextArea } from '@dealstack/frontend/shared/components/forms/TextArea/TextArea';
import { useDeclarationsEditForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/useDeclarationsEditForm';
import { DeclarationsFormField } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/DeclarationsEditForm.types';
import { FUNDS_CHECKBOXES } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/declarationsEditForm.utils';

interface Props {
	currentInvestor?: GetKYCInvestorInstanceRes;
	kycSetting?: KycWorkflowSettingResponseDto;
	onCancel: () => void;
	onSaved: () => void;
}

export const DeclarationsEditForm = ({ currentInvestor, kycSetting, onCancel, onSaved }: Props) => {
	const tInvestor = useTranslationInvestor();
	const tMipManage = useTranslationMipManage();
	const {
		form,
		onSubmit,
		isSaving,
		isBeneficialOwnershipRequired,
		isSourceOfFundsRequired,
		isPepRequired,
	} = useDeclarationsEditForm({ currentInvestor, kycSetting, onSaved });

	const isPep = form.watch(DeclarationsFormField.IS_POLITICALLY_EXPOSED_PERSON);
	const showSourceOfFundsTextarea = !!form
		.watch(DeclarationsFormField.SOURCE_OF_FUNDS)
		?.includes(DeclarationType.SOURCE_OF_FUNDS_OTHER);
	const pepError = form.formState.errors[DeclarationsFormField.IS_POLITICALLY_EXPOSED_PERSON];

	let sectionCounter = 0;
	const boSectionNumber = isBeneficialOwnershipRequired ? ++sectionCounter : null;
	const sofSectionNumber = isSourceOfFundsRequired ? ++sectionCounter : null;
	const pepSectionNumber = isPepRequired ? ++sectionCounter : null;

	return (
		<FormProvider {...form}>
			<form onSubmit={onSubmit} style={{ maxWidth: '40rem', width: '100%' }}>
				<Flex direction="column" gap="1.5rem">
					{isBeneficialOwnershipRequired && (
						<Flex direction="column" gap="0.75rem">
							<Flex align="center" gap="0.5rem">
								<Circle variant="filled">
									<MantineText c="gray.0" fz="lg" fw={600}>
										{boSectionNumber}
									</MantineText>
								</Circle>
								<Title order={5}>
									{tInvestor('investment.join.declaration.beneficial_ownership')}
								</Title>
							</Flex>
							<GreyBox>
								<FormCheckbox
									name={DeclarationsFormField.BENEFICIAL_OWNERSHIP}
									label={tInvestor(
										`investment.join.kyc.declarations.beneficial.${
											currentInvestor?.piv ? 'label_piv' : 'label_individual'
										}`
									)}
								/>
							</GreyBox>
						</Flex>
					)}

					{isSourceOfFundsRequired && (
						<Flex direction="column" gap="0.75rem">
							<Flex align="center" gap="0.5rem">
								<Circle variant="filled">
									<MantineText c="gray.0" fz="lg" fw={600}>
										{sofSectionNumber}
									</MantineText>
								</Circle>
								<Title order={5}>
									{tInvestor('investment.join.declaration.source_funds')}
								</Title>
							</Flex>
							<GreyBox>
								<Title order={5} mb="0.5rem">
									{tInvestor('investment.join.declaration.source_funds_descr')}
								</Title>
								<CheckboxGroupV2 name={DeclarationsFormField.SOURCE_OF_FUNDS}>
									<Flex direction="column" gap="0.5rem">
										{FUNDS_CHECKBOXES.map((checkbox) => (
											<MantineCheckbox
												key={checkbox.value}
												value={checkbox.value}
												label={tInvestor(checkbox.labelKey)}
											/>
										))}
									</Flex>
								</CheckboxGroupV2>
								{showSourceOfFundsTextarea && (
									<TextArea
										label={tInvestor(
											'investment.join.kyc.declarations.pep.details_label'
										)}
										name={DeclarationsFormField.SOURCE_OF_FUNDS_OTHER_DETAILS}
										rows={2}
										fullWidth
									/>
								)}
							</GreyBox>
						</Flex>
					)}

					{isPepRequired && (
						<Flex direction="column" gap="0.75rem">
							<Flex align="center" gap="0.5rem">
								<Circle variant="filled">
									<MantineText c="gray.0" fz="lg" fw={600}>
										{pepSectionNumber}
									</MantineText>
								</Circle>
								<Title order={5}>
									{tInvestor('investment.join.declaration.pep')}
								</Title>
							</Flex>
							<GreyBox>
								<Title order={5} mb="0.5rem">
									{tInvestor('investment.join.declaration.pep_question')}
								</Title>
								<Flex direction="column" gap="0.5rem">
									<MantineCheckbox
										checked={isPep === false}
										onClick={() =>
											form.setValue(
												DeclarationsFormField.IS_POLITICALLY_EXPOSED_PERSON,
												isPep === false ? undefined : false,
												{ shouldValidate: true }
											)
										}
										label={tInvestor(
											'investment.join.kyc.declarations.pep.label_1'
										)}
										disabled={form.watch(
											DeclarationsFormField.IS_POLITICALLY_EXPOSED_PERSON
										)}
									/>
									<MantineCheckbox
										checked={isPep}
										onClick={() =>
											form.setValue(
												DeclarationsFormField.IS_POLITICALLY_EXPOSED_PERSON,
												isPep ? undefined : true,
												{ shouldValidate: true }
											)
										}
										label={tInvestor(
											'investment.join.kyc.declarations.pep.label_2'
										)}
										disabled={
											form.watch(
												DeclarationsFormField.IS_POLITICALLY_EXPOSED_PERSON
											) === false
										}
									/>
									{!!pepError?.message && (
										<MantineText c="red.6">{pepError.message}</MantineText>
									)}
								</Flex>
								{isPep && (
									<TextArea
										label={tInvestor(
											'investment.join.kyc.declarations.pep.details_label'
										)}
										name={DeclarationsFormField.PEP_DETAILS}
										rows={4}
										fullWidth
									/>
								)}
							</GreyBox>
						</Flex>
					)}

					<Flex gap="0.5rem">
						<Button
							variant="outline"
							color="dark"
							onClick={onCancel}
							disabled={isSaving}
						>
							{tMipManage('kyc.declarations.edit.cancel')}
						</Button>
						<Button type="submit" loading={isSaving}>
							{tMipManage('kyc.declarations.edit.save')}
						</Button>
					</Flex>
				</Flex>
			</form>
		</FormProvider>
	);
};
